import React from "react";

import {
  shades,
  avatar,
  background,
  gradients,
  opacity,
  transparent,
} from "@app/common/theme/colors";

type ShadeColors = { [index: number]: React.CSSProperties["color"] };
type ShadesMap = {
  [Property in keyof typeof shades]: ShadeColors;
};

type AvatarColors = {
  [index: string]: {
    main: React.CSSProperties["color"];
    dark: React.CSSProperties["color"];
  };
};
type ChipColors = {
  icon: { [index: string]: React.CSSProperties["color"] };
  background: { [index: string]: React.CSSProperties["color"] };
};

type BoxShadowSize = "sm" | "md" | "lg" | "xl";
type BoxShadows = {
  [k in keyof BoxShadowSize]: React.CSSProperties["boxShadow"];
};
type TransparentMap = {
  [k in keyof typeof transparent]: {
    [index: string]: React.CSSProperties["color"];
  };
};
type StatesMap = {
  [index: string]: { [index: string]: React.CSSProperties["color"] };
};
type ChartColors = {
  [index: string]: { [index: string]: React.CSSProperties["color"] };
};

type ScoreMap = Record<
  "positive" | "negative" | "neutral" | "unknown",
  React.CSSProperties["color"]
>;

type Gradients = { [index: string]: React.CSSProperties["background"] };

declare module "@mui/material/styles/createPalette" {
  interface TypeBackground {
    navbar?: React.CSSProperties["color"];
    sidenav?: React.CSSProperties["background"];
    emblem?: React.CSSProperties["background"];
  }
}

declare module "@mui/material/Chip" {
  interface ChipPropsColorOverrides {
    positive: true;
    negative: true;
    neutral: true;
  }
}

declare module "@mui/material/Button" {
  interface ButtonPropsColorOverrides {
    default: true;
    cancel: true;
  }
}

declare module "@mui/material/IconButton" {
  interface IconButtonPropsColorOverrides {
    cancel: true;
  }
}

declare module "@mui/material/styles" {
  interface Palette {
    link: {
      main: React.CSSProperties["color"];
    };
    overlay: {
      primary: ShadeColors;
    };
    shades: ShadesMap;
    transparent: TransparentMap;
    scores: ScoreMap;
    avatar: AvatarColors;
    chips: ChipColors;
    boxShadows: BoxShadows;
    chart: ChartColors;
    states: StatesMap;
    gradients: Gradients;
  }
}

const palette = () => ({
  palette: {
    primary: {
      main: "#4C47CC",
      light: "#9491E0",
      dark: "#3D39A3",
    },
    secondary: {
      main: "#4462EF",
      light: "#8FA1F5",
      dark: "#364EBF",
    },
    cancel: {
      main: "#E0E0E0",
      light: "#F0F0F0",
      dark: "#C1C1C1",
    },
    error: {
      main: "#B23128",
      light: "#E63F34",
      dark: "#80231D",
    },
    warning: {
      main: "#D98C4C",
      light: "#E66803",
      dark: "#803A01",
    },
    info: {
      main: "#3A54CC",
      light: "#4969FF",
      dark: "#2C3F99",
    },
    success: {
      main: "#007E33",
      light: "#00B348",
      dark: "#004D1F",
    },
    avatar: avatar,
    shades: shades,
    transparent,
    chips: {
      positive: {
        icon: "#44B36A",
        background: "#F2FAF2",
      },
      neutral: {
        icon: "#2F2854",
        background: "#F2EFFF",
      },
      negative: {
        icon: "#B34444",
        background: "#FCF3F1",
      },
    },
    link: {
      main: "#4462EF",
    },
    gradients,
    background,
    boxShadows: {
      xl: "box-shadow: 0px 32px 32px -8px rgba(0, 0, 0, 0.08), 0px 0px 32px -8px rgba(0, 0, 0, 0.12), 0px 0px 4px rgba(0, 0, 0, 0.02)",
      lg: "box-shadow: 0px 8px 24px -6px rgba(0, 0, 0, 0.16), 0px 0px 1px rgba(0, 0, 0, 0.04)",
      md: "box-shadow: 0px 8px 12px -6px rgba(0, 0, 0, 0.16), 0px 0px 1px rgba(0, 0, 0, 0.04)",
      sm: "box-shadow: 0px 6px 8px -6px rgba(0, 0, 0, 0.16), 0px 0px 1px rgba(0, 0, 0, 0.04)",
    },
    overlay: {
      primary: {
        4: "#17161B",
        12: "#1F1B2E",
        16: "#231F38",
        24: "#2B254B",
        28: "#2F2854",
      },
    },
    opacity,
    scores: {
      positive: "#19C79F",
      neutral: "#656565",
      negative: "#EF4E77",
      unknown: "#E0E0E0",
    },
    chart: {
      blue: {
        light: "#708FFA",
      },
      purple: {
        main: "#6B67E5",
        light: "#9996fa",
        dark: "#4C47CC",
      },
      orange: {
        main: "#F4A665",
        light: "#F4BF93",
        dark: "#D98C4C",
      },
      pink: {
        main: "#FC668D",
        light: "#FC97B1",
      },
      teal: {
        main: "#3CC4DC",
        light: "#A8E5F0",
        dark: "#25B3CC",
      },
      green: {
        main: "#19C79F",
        light: "#57D9BB",
        dark: "#15A684",
      },
      yellow: {
        main: "#F4D565",
        light: "#F4DF93",
        dark: "#D9BA4C",
      },
    },
    states: {
      hover: "#F5F7FD",
      selected: "#FAFAFA",
    },
  },
});

export default palette;
